import { Component, OnInit, ChangeDetectionStrategy, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { noop, Observable, tap } from 'rxjs';

import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import moment from 'moment';
import { NewPermission } from '@core/enums/permission';
import { Appointment } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';

@Component({
  selector: 'project-panel-dates',
  templateUrl: './dates.component.html',
  styleUrls: ['./dates.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsDatesComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  editMode: boolean;
  titleFieldDef: any;
  panelName: string;
  changeTitleMode: boolean = false;
  keyDatesForm = new UntypedFormGroup({ keyDates: new UntypedFormArray([]) });
  ui = { isLoading: true };
  NewPermission = NewPermission;

  get keyDates(): UntypedFormArray {
    return this.keyDatesForm.controls['keyDates'] as UntypedFormArray;
  }

  constructor(
    private readonly projectsService: ProjectsService,
    protected readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super(changeDetectorRef);
  }

  ngOnInit(): void {
    this.titleFieldDef = {
      name: 'dates.panel_name',
      label: this.panelDef.title,
      type: 'text',
    };

    this.projectsService
      .getAppointments()
      .pipe(
        untilComponentDestroyed(this),
        tap((appointments: Appointment[]) => {
          for (const appointment of appointments) {
            this.keyDates.push(
              new UntypedFormGroup({
                label: new UntypedFormControl(appointment.title),
                date: new UntypedFormControl(moment(appointment.start_date).utc()),
                hidden: new UntypedFormControl(appointment),
              }),
            );
          }

          this.ui = { ...this.ui, isLoading: false };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  getOverdueClass(index: number): string {
    let startDate: moment.Moment;

    if (!(startDate = this.keyDates.at(index).get('date').value)) {
      return null;
    }

    return startDate.isBefore(moment(), 'days') ? 'overdue' : 'due-set';
  }

  add(): void {
    this.keyDates.push(
      new UntypedFormGroup({
        label: new UntypedFormControl(''),
        date: new UntypedFormControl(null),
        hidden: new UntypedFormControl(null),
      }),
    );
  }

  remove(index: number): void {
    const appointment = this.keyDates.at(index).get('hidden').value as Appointment;

    if (!appointment) {
      this.keyDates.removeAt(index);
      return;
    }

    this.projectsService
      .deleteAppointment(appointment.id)
      .pipe(
        untilComponentDestroyed(this),
        tap(() => this.keyDates.removeAt(index)),
      )
      .subscribe(noop);
  }

  save(index: number): void {
    const appointment = this.keyDates.at(index).get('hidden').value as Appointment;
    const label = this.keyDates.at(index).get('label').value as string;
    const date = this.keyDates.at(index).get('date').value as moment.Moment;

    let appointmentObservable: Observable<Appointment>;

    if (!appointment) {
      appointmentObservable = this.projectsService.createAppointment({
        title: label,
        start_date: date?.utc().startOf('day').toISOString(),
        related_to_type: 'Project',
        related_to_id: Number(this.project.id),
      } as Appointment);
    } else {
      appointmentObservable = this.projectsService.updateAppointment({
        ...appointment,
        title: label,
        start_date: date?.utc().startOf('day').toISOString(),
      });
    }

    appointmentObservable
      .pipe(
        untilComponentDestroyed(this),
        tap((appointment) => this.keyDates.at(index).get('hidden').setValue(appointment)),
      )
      .subscribe(noop);
  }
}
